/**
 * ONE fetch ceiling for the ICM list screens, plus the notice that fires when a
 * list actually reaches it.
 *
 * WHY THIS EXISTS. Every ICM list query defaults to `Take = 50` server-side, and
 * the shared `<AppDataGrid>` pages on the CLIENT — it slices the rows it was
 * given. A page that sends no `take` therefore does not get "page 1 of many";
 * it gets the first 50 rows, full stop, and its footer reports 50 as the TOTAL.
 * Owner report 2026-10-01, Item Requisition History: GoLive showed
 * "Page 1 of 73 (1455 items)", AI showed "Page 1 of 3 (50 items)" — 1,524 rows
 * in the database, 50 on screen, and no way to reach the rest by paging,
 * searching, sorting or column-filtering, because the other 1,474 were never
 * fetched.
 *
 * WHY A BIG NUMBER IS SAFE HERE. The SP computes the whole result set either
 * way; OFFSET/FETCH only trims what is sent. Measured on the live division:
 * 8,389 Distribution rows return in 250 ms against 190 ms for 500, and 1,091
 * consumption rows in 55 ms. Well inside the section 12.5 budget (10k-row grid
 * p95 < 1500 ms). The ceiling is kept as a guard against a pathological set, not
 * as a page size — which is exactly how 50 and 500 came to be mistaken for one.
 *
 * WHEN THE CEILING IS HIT the screen SAYS SO rather than showing a silent
 * prefix, which is how the 50 went unnoticed in the first place. Every ICM list
 * SP returns the true count (`COUNT(*) OVER ()`, evaluated before OFFSET/FETCH)
 * and the API surfaces it as `PagedResult.Total`.
 */
export const ICM_LIST_TAKE = 20000;

/**
 * The message to show when a list came back short, or `null` when it did not.
 *
 * @param loaded how many rows the page actually received
 * @param total  the server's true row count (`PagedResult.Total`); a non-number
 *               means the endpoint does not report one, and nothing is claimed
 */
export function icmTruncationNote(loaded: number, total: unknown): string | null {
  if (typeof total !== 'number' || loaded >= total) return null;
  return `Showing the first ${loaded} of ${total} records. `
       + 'Narrow the list with the Date Filter or search.';
}
